import React, { FunctionComponent, ReactNode } from 'react'
import { Avatar, Button, Text } from '@afterpaytouch/core'
import { useTranslation } from 'next-i18next'
import { useConsumerSizes } from '../../../utils/responsive'
import styles from './style.module.css'
import { useAppRouter, useConsumerName, useConsumerEmail, useConsumerSelector, useConsumerCountry } from '../../../state'
import { Route } from '../../../config/router'
import { isCountryHardIdvSkipDocumentSelection } from '../../../model/identity'
import { InfoModal, InfoModalProps } from './infoModal'
import { isUndefinedOrEmpty } from '@afterpay/utils'
import clsx from 'clsx'
import { convertConsumerDateOfBirth } from '../../../state/account/consumer/helper'
import { VerifyLaterModal } from '../VerifyLaterModal/VerifyLaterModal'
import { useAmplitudeWithEnduringEventProperties } from '../../../integrations/amplitude'
import { TrackingEvent } from '../../../model/amplitude'

export const VerifyStart: FunctionComponent = () => {
  const { t } = useTranslation()
  const { textSize, smallTextSize, sectionHeadingSize } = useConsumerSizes()
  const consumer = useConsumerSelector()
  const dateOfBirth = consumer?.dateOfBirth
  const consumerName = useConsumerName()
  const email = useConsumerEmail()
  const router = useAppRouter()
  const country = useConsumerCountry()

  const hasTranslation = (key: string): boolean => {
    const translation = t(key, { defaultValue: '' })
    return translation !== '' && translation !== key && !isUndefinedOrEmpty(translation)
  }

  const [showVerifyLaterModal, setShowVerifyLaterModal] = React.useState(false)
  const { logEvent } = useAmplitudeWithEnduringEventProperties()

  const handleStart = (): void => {
    const skipDocumentSelection = isCountryHardIdvSkipDocumentSelection(country)
    logEvent(TrackingEvent.PRESSED_UPLOAD_MY_ID_BUTTON_ON_IDENTITY_START_PAGE)
    skipDocumentSelection ? router.push(Route.VERIFY_BIOMETRIC_PERSONA) : router.push(Route.VERIFY_IDENTITY)
  }

  const handleVerifyLaterClick = (): void => {
    logEvent(TrackingEvent.PRESSED_VERIFY_LATER_BUTTON)
    setShowVerifyLaterModal(true)
  }
  const handleModalClose = (): void => setShowVerifyLaterModal(false)
  const handleVerifyNowClick = (): void => {
    logEvent(TrackingEvent.PRESSED_VERIFY_NOW_BUTTON)
    setShowVerifyLaterModal(false)
  }
  const handleSkipClick = (): void => {
    logEvent(TrackingEvent.PRESSED_SKIP_BUTTON)
    setShowVerifyLaterModal(false)
    router.push(Route.HOME)
  }

  const renderDocumentList = (): ReactNode => {
    const documentListOptions: Array<{ content: string; info?: InfoModalProps }> = t('verifyIdentity:start:documents', {
      returnObjects: true,
      defaultValue: [],
    })
    if (!Array.isArray(documentListOptions) || documentListOptions.length === 0) {
      return null
    }

    return (
      <ul className={styles.documentList}>
        {documentListOptions.map(
          (option, i) =>
            option !== null &&
            'content' in option && (
              <li key={`verify-start-document-${i}`} className={clsx(styles.documentItem, option.info != null && styles.documentItemWithInfo)}>
                <div className={styles.documentItemContent}>
                  <Text size={textSize}>{option.content}</Text>
                  {option.info != null && <InfoModal {...option.info} />}
                </div>
              </li>
            )
        )}
      </ul>
    )
  }

  return (
    <div className={styles.container}>
      <div className={styles.profileCard}>
        <Avatar size='M' src='' alt={consumerName} name={consumerName} title={consumerName} />
        <div className='info'>
          <Text bold size={sectionHeadingSize}>
            {consumerName}
          </Text>
          <Text size={smallTextSize} color='Gray50'>
            {convertConsumerDateOfBirth({ dateOfBirth, locale: router.locale })}
          </Text>
          <Text size={smallTextSize} color='Gray50'>
            {email}
          </Text>
        </div>
      </div>
      <div className={styles.description}>
        <Text size={textSize}>{t('verifyIdentity:start:description')}</Text>
      </div>
      <Text size={textSize}>{t('verifyIdentity:start:detail')}</Text>
      {renderDocumentList()}
      {hasTranslation('verifyIdentity:start:reminder') && (
        <div className={styles.reminder}>
          <Text size={smallTextSize} color='Gray50'>
            {t('verifyIdentity:start:reminder')}
          </Text>
        </div>
      )}
      <Button.Ghost testNameSpace='verify-start-verify-later' padding='Fluid' onClick={handleVerifyLaterClick} size='Regular'>
        {t('verifyIdentity:verifyLater:buttonText')}
      </Button.Ghost>
      <Button.Primary testNameSpace='verify-verify-start-upload' padding='Fluid' onClick={handleStart} size='Regular'>
        {t('verifyIdentity:start:cta')}
      </Button.Primary>
      <VerifyLaterModal show={showVerifyLaterModal} onClose={handleModalClose} onVerifyNow={handleVerifyNowClick} onSkip={handleSkipClick} />
    </div>
  )
}
